import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import logoImg from "@/assets/logo.png";

const LINKS = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/services", label: "Services" },
  { to: "/careers", label: "Careers" },
  { to: "/contact", label: "Contact" },
];

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  return (
    <header className="fixed inset-x-0 top-0 z-50">
      <div className="hidden md:flex items-center justify-between bg-ink text-cream px-6 lg:px-10 py-2 text-[11px] font-semibold uppercase tracking-[0.2em]">
        <span>Your Road to Reliance</span>
        <a
          href="tel:+18338123828"
          data-testid="navbar-top-phone-link"
          className="flex items-center gap-2 transition-colors duration-200 hover:text-brand-gold"
        >
          <Phone size={12} aria-hidden="true" />
          833-V1aduct · 833-812-3828
        </a>
      </div>

      <nav className="flex items-center justify-between h-[72px] bg-cream/95 backdrop-blur border-b-[3px] border-ink px-4 md:px-6 lg:px-10">
        <Link to="/" data-testid="navbar-logo-link" className="flex items-center gap-3">
          <img
            src={logoImg}
            alt="Memphis Viaduct Inc badge"
            className="h-11 w-11 md:h-12 md:w-12 rounded-full object-cover border-[3px] border-ink shadow-[3px_3px_0_#111] bg-cream"
          />
          <span className="leading-none">
            <span className="block font-display text-xl md:text-2xl tracking-tight">MEMPHIS VIADUCT</span>
            <span className="block text-[10px] font-bold uppercase tracking-[0.3em] text-brand-red mt-1">
              Inc · Est. 2025
            </span>
          </span>
        </Link>

        <div className="hidden md:flex items-center gap-6 lg:gap-8">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`navbar-${l.label.toLowerCase()}-link`}
              className={({ isActive }) =>
                `relative py-1 text-sm font-bold uppercase tracking-[0.15em] transition-colors duration-200 hover:text-brand-red ${
                  isActive ? "text-brand-red" : "text-ink"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <Link
            to="/careers"
            data-testid="navbar-drive-cta"
            className="inline-flex items-center gap-2 border-[3px] border-ink bg-brand-red text-cream px-5 py-2.5 font-display uppercase tracking-wide text-base leading-none shadow-[4px_4px_0_#111] transition-[transform,box-shadow,background-color] duration-200 hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-[2px_2px_0_#111] hover:bg-brand-reddeep"
          >
            Drive With Us
          </Link>
        </div>

        <button
          className="md:hidden p-2 border-[3px] border-ink bg-cream shadow-[3px_3px_0_#111] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none transition-[transform,box-shadow] duration-150"
          onClick={() => setOpen((v) => !v)}
          data-testid="navbar-menu-toggle"
          aria-label="Toggle menu"
          aria-expanded={open}
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.25 }}
            className="md:hidden bg-ink text-cream border-b-[3px] border-ink px-6 py-6"
            data-testid="navbar-mobile-menu"
          >
            {LINKS.map((l, i) => (
              <motion.div
                key={l.to}
                initial={{ opacity: 0, x: -12 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.05 * i }}
              >
                <NavLink
                  to={l.to}
                  className={({ isActive }) =>
                    `block py-3 font-display text-3xl uppercase border-b border-cream/15 ${
                      isActive ? "text-brand-gold" : ""
                    }`
                  }
                >
                  {l.label}
                </NavLink>
              </motion.div>
            ))}
            <a href="tel:+18338123828" data-testid="navbar-mobile-phone-link" className="block mt-6 font-display text-2xl text-brand-gold">
              833-V1ADUCT
            </a>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Navbar;
